<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  AcademicCapIcon,
  CheckCircleIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import type { Catalog } from '@/types';
import { useRoleRequest } from '@/composables/useRoleRequest';
import { useNotificationStore } from '@/store/notifications';

const { t } = useI18n();
const notify = useNotificationStore();

const {
  modalOpen,
  submitting,
  error: submitError,
  requestableUserTypes,
  specialtyCatalog,
  closeModalForVisit,
  submitRequest,
} = useRoleRequest();

const selectedTypeId = ref<number | ''>('');
const selectedSpecialties = ref<Catalog[]>([]);
const searchQuery = ref('');
const closeButtonRef = ref<HTMLButtonElement | null>(null);

const selectedType = computed(() =>
  requestableUserTypes.value.find((type) => type.id === selectedTypeId.value) ?? null,
);

const specialtiesRequired = computed(() => {
  const code = (selectedType.value?.code || '').trim().toUpperCase();
  return code !== 'MONITOR';
});

const filteredSpecialties = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  const items = [...specialtyCatalog.value].sort((a, b) => a.name.localeCompare(b.name, 'es'));
  if (!query) return items;
  return items.filter((item) =>
    item.name.toLowerCase().includes(query)
    || item.code.toLowerCase().includes(query),
  );
});

const canSend = computed(() =>
  selectedTypeId.value !== ''
  && (specialtiesRequired.value ? selectedSpecialties.value.length > 0 : true)
  && !submitting.value,
);

const selectedTypeName = computed(() => selectedType.value?.name ?? '');

const selectionSummary = computed(() =>
  t('roleRequest.modal.selectionSummary', {
    role: selectedTypeName.value || t('roleRequest.modal.noRoleSelected'),
    count: selectedSpecialties.value.length,
  }),
);

function isSelected(id: number) {
  return selectedSpecialties.value.some((s) => s.id === id);
}

function toggleSpecialty(item: Catalog) {
  if (isSelected(item.id)) {
    selectedSpecialties.value = selectedSpecialties.value.filter((s) => s.id !== id);
  } else {
    selectedSpecialties.value = [...selectedSpecialties.value, item];
  }
}

function resetForm() {
  selectedTypeId.value = '';
  selectedSpecialties.value = [];
  searchQuery.value = '';
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && modalOpen.value) {
    event.preventDefault();
    closeModalForVisit();
  }
}

watch(selectedTypeId, () => {
  if (!specialtiesRequired.value) {
    selectedSpecialties.value = [];
    searchQuery.value = '';
  }
});

watch(modalOpen, async (open) => {
  if (open) {
    document.body.style.overflow = 'hidden';
    await nextTick();
    closeButtonRef.value?.focus();
  } else {
    document.body.style.overflow = '';
    resetForm();
  }
});

onUnmounted(() => {
  document.body.style.overflow = '';
});

async function onSubmit() {
  if (!canSend.value || selectedTypeId.value === '') return;

  const ok = await submitRequest(
    Number(selectedTypeId.value),
    selectedSpecialties.value.map((s) => s.id),
  );

  if (ok) {
    notify.success(
      t('roleRequest.modal.successTitle'),
      t('roleRequest.modal.successMessage'),
    );
  } else if (submitError.value === 'RATE_LIMIT') {
    notify.error(
      t('roleRequest.modal.errorTitle'),
      t('roleRequest.modal.rateLimitError'),
    );
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="role-request-fade-enter-active"
      enter-from-class="role-request-fade-enter-from"
      enter-to-class="role-request-fade-enter-to"
      leave-active-class="role-request-fade-leave-active"
      leave-from-class="role-request-fade-leave-from"
      leave-to-class="role-request-fade-leave-to"
    >
      <div
        v-if="modalOpen"
        class="role-request-overlay fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-4 md:p-6"
        @keydown="onKeydown"
      >
        <div
          class="absolute inset-0 modal-scrim backdrop-blur-sm motion-reduce:backdrop-blur-none"
          aria-hidden="true"
        />

        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="role-request-title"
          aria-describedby="role-request-desc"
          class="role-request-dialog relative flex w-full flex-col overflow-hidden
                 h-[100dvh] max-h-[100dvh] rounded-none
                 sm:h-auto sm:max-h-[92dvh] sm:max-w-2xl sm:rounded-2xl
                 md:max-w-3xl
                 lg:max-w-5xl lg:rounded-3xl"
        >
          <header class="role-request-header shrink-0 px-4 py-4 sm:px-5 md:px-6">
            <div class="flex items-start gap-3 sm:gap-4">
              <div
                class="role-request-icon-wrap flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl sm:h-12 sm:w-12"
                aria-hidden="true"
              >
                <UserGroupIcon class="h-6 w-6 sm:h-7 sm:w-7" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">
                  {{ t('roleRequest.modal.badge') }}
                </p>
                <h2
                  id="role-request-title"
                  class="mt-1 text-lg font-bold leading-tight text-app-primary sm:text-xl md:text-2xl"
                >
                  {{ t('roleRequest.modal.title') }}
                </h2>
                <p
                  id="role-request-desc"
                  class="mt-1 text-sm leading-relaxed text-app-secondary"
                >
                  {{ t('roleRequest.modal.subtitle') }}
                </p>
              </div>
              <button
                ref="closeButtonRef"
                type="button"
                class="role-request-close flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-secondary"
                :aria-label="t('roleRequest.modal.closeHint')"
                @click="closeModalForVisit"
              >
                <XMarkIcon class="h-6 w-6" />
              </button>
            </div>
          </header>

          <div class="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5 md:px-6">
            <div class="grid gap-5 lg:grid-cols-2 lg:gap-6">
              <section aria-labelledby="role-request-step-type">
                <div class="mb-3 flex items-center gap-2">
                  <span class="role-request-step-badge flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold">
                    1
                  </span>
                  <h3 id="role-request-step-type" class="text-lg font-semibold text-app-primary sm:text-xl">
                    {{ t('roleRequest.modal.userTypeLabel') }}
                  </h3>
                </div>
                <p class="mb-4 text-base text-app-tertiary">
                  {{ t('roleRequest.modal.userTypeHint') }}
                </p>

                <fieldset>
                  <legend class="sr-only">{{ t('roleRequest.modal.userTypeLabel') }}</legend>
                  <div
                    role="radiogroup"
                    aria-labelledby="role-request-step-type"
                    class="grid gap-3"
                  >
                    <button
                      v-for="type in requestableUserTypes"
                      :key="type.id"
                      type="button"
                      role="radio"
                      :aria-checked="selectedTypeId === type.id"
                      class="role-request-option min-h-[4.5rem] rounded-2xl px-4 py-4 text-left transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                      :class="{ 'is-selected': selectedTypeId === type.id }"
                      @click="selectedTypeId = type.id"
                    >
                      <span class="flex items-start gap-3">
                        <CheckCircleIcon
                          class="role-request-option-icon mt-0.5 h-6 w-6 shrink-0"
                          :class="{ 'text-brand-primary': selectedTypeId === type.id }"
                        />
                        <span>
                          <span class="block text-base font-semibold sm:text-lg">{{ type.name }}</span>
                          <span
                            v-if="type.description"
                            class="role-request-option-desc mt-1 block text-sm leading-relaxed sm:text-base"
                          >
                            {{ type.description }}
                          </span>
                        </span>
                      </span>
                    </button>
                  </div>
                </fieldset>
              </section>

              <section aria-labelledby="role-request-step-specialties">
                <div class="mb-3 flex items-center gap-2">
                  <span class="role-request-step-badge role-request-step-badge--secondary flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold">
                    2
                  </span>
                  <h3 id="role-request-step-specialties" class="text-lg font-semibold text-app-primary sm:text-xl">
                    {{ t('roleRequest.modal.specialtiesLabel') }}
                  </h3>
                </div>
                <p class="mb-4 text-base text-app-tertiary">
                  {{ specialtiesRequired ? t('roleRequest.modal.specialtiesHint') : t('roleRequest.modal.specialtiesNotRequired') }}
                </p>

                <p
                  v-if="!specialtiesRequired"
                  class="role-request-summary rounded-2xl px-4 py-3 text-sm"
                >
                  {{ t('roleRequest.modal.specialtiesSkipped') }}
                </p>

                <template v-else>

                <div
                  v-if="selectedSpecialties.length > 0"
                  class="role-request-selected-panel mb-4 rounded-2xl p-3 sm:p-4"
                  aria-live="polite"
                >
                  <p class="mb-2 text-sm font-medium text-brand-secondary sm:text-base">
                    {{ t('roleRequest.modal.selectedSpecialties') }}
                  </p>
                  <div class="flex flex-wrap gap-2">
                    <span
                      v-for="item in selectedSpecialties"
                      :key="item.id"
                      class="role-request-chip inline-flex min-h-11 items-center gap-2 rounded-xl px-3 py-2 text-sm sm:text-base"
                    >
                      {{ item.name }}
                      <button
                        type="button"
                        class="role-request-chip-btn flex h-8 w-8 items-center justify-center rounded-lg transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-secondary"
                        :aria-label="t('roleRequest.modal.removeSpecialty', { name: item.name })"
                        @click="toggleSpecialty(item)"
                      >
                        <XMarkIcon class="h-4 w-4" />
                      </button>
                    </span>
                  </div>
                </div>

                <div class="role-request-picker overflow-hidden rounded-2xl">
                  <div class="role-request-picker-header p-3 sm:p-4">
                    <label class="sr-only" for="role-request-specialty-search">
                      {{ t('roleRequest.modal.searchSpecialties') }}
                    </label>
                    <div class="relative">
                      <MagnifyingGlassIcon class="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-app-muted" />
                      <input
                        id="role-request-specialty-search"
                        v-model="searchQuery"
                        type="search"
                        :placeholder="t('roleRequest.modal.searchSpecialties')"
                        class="input-field min-h-12 w-full pl-12 text-base sm:text-lg"
                        autocomplete="off"
                      />
                    </div>
                  </div>

                  <ul
                    class="max-h-56 divide-y divide-[var(--color-divider)] overflow-y-auto sm:max-h-64 md:max-h-72"
                    :aria-label="t('roleRequest.modal.specialtiesLabel')"
                  >
                    <li v-for="item in filteredSpecialties" :key="item.id">
                      <button
                        type="button"
                        class="role-request-picker-item flex min-h-[3.25rem] w-full items-center gap-3 px-4 py-3 text-left text-base transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-brand-secondary sm:min-h-14 sm:px-5 sm:text-lg"
                        :class="{ 'is-selected': isSelected(item.id) }"
                        :aria-pressed="isSelected(item.id)"
                        @click="toggleSpecialty(item)"
                      >
                        <AcademicCapIcon class="h-5 w-5 shrink-0 text-brand-secondary sm:h-6 sm:w-6" />
                        <span class="min-w-0 flex-1 font-medium">{{ item.name }}</span>
                        <CheckIcon
                          v-if="isSelected(item.id)"
                          class="h-5 w-5 shrink-0 text-brand-secondary sm:h-6 sm:w-6"
                          aria-hidden="true"
                        />
                      </button>
                    </li>
                  </ul>

                  <p
                    v-if="filteredSpecialties.length === 0"
                    class="px-4 py-6 text-center text-sm text-app-muted"
                  >
                    {{ t('roleRequest.modal.noSpecialties') }}
                  </p>
                </div>
                </template>
              </section>
            </div>

            <p
              class="role-request-summary mt-5 rounded-2xl px-4 py-3 text-sm"
              aria-live="polite"
            >
              {{ selectionSummary }}
            </p>
          </div>

          <footer class="role-request-footer shrink-0 px-4 py-3 sm:px-5 md:px-6">
            <p
              v-if="submitError"
              class="mb-3 rounded-xl border border-brand-primary/30 bg-brand-primary/10 px-4 py-3 text-sm text-brand-primary-light"
              role="alert"
            >
              {{ submitError === 'RATE_LIMIT' ? t('roleRequest.modal.rateLimitError') : submitError }}
            </p>
            <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p class="text-sm text-app-tertiary sm:max-w-md sm:text-base">
                {{ t('roleRequest.modal.footerHint') }}
              </p>
              <div class="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  class="btn-secondary min-h-12 w-full px-5 text-base sm:w-auto sm:min-w-[10rem] sm:text-lg"
                  :disabled="submitting"
                  @click="closeModalForVisit"
                >
                  {{ t('roleRequest.modal.continueBrowsing') }}
                </button>
                <AsyncButton
                  type="button"
                  variant="primary"
                  class="min-h-12 w-full px-5 text-base sm:w-auto sm:min-w-[12rem] sm:text-lg"
                  :disabled="!canSend"
                  :loading="submitting"
                  :action="onSubmit"
                >
                  {{ submitting ? t('roleRequest.modal.submitting') : t('roleRequest.modal.submit') }}
                </AsyncButton>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.role-request-overlay {
  padding-bottom: env(safe-area-inset-bottom);
}

@media (min-width: 640px) {
  .role-request-overlay {
    padding-bottom: 0;
  }
}

.role-request-fade-enter-active,
.role-request-fade-leave-active {
  transition: opacity 0.22s ease;
}
.role-request-fade-enter-active .role-request-dialog,
.role-request-fade-leave-active .role-request-dialog {
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}
.role-request-fade-enter-from,
.role-request-fade-leave-to {
  opacity: 0;
}
.role-request-fade-enter-from .role-request-dialog,
.role-request-fade-leave-to .role-request-dialog {
  transform: translateY(2.25rem);
}
@media (min-width: 640px) {
  .role-request-fade-enter-from .role-request-dialog,
  .role-request-fade-leave-to .role-request-dialog {
    transform: translateY(0.65rem) scale(0.97);
  }
}
@media (prefers-reduced-motion: reduce) {
  .role-request-fade-enter-active,
  .role-request-fade-leave-active,
  .role-request-fade-enter-active .role-request-dialog,
  .role-request-fade-leave-active .role-request-dialog {
    transition-duration: 0.01ms !important;
  }
  .role-request-fade-enter-from .role-request-dialog,
  .role-request-fade-leave-to .role-request-dialog {
    transform: none;
  }
}
</style>
